import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { NgClass, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { FileUploadModule } from 'ng2-file-upload';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { ContactDataType } from 'src/app/dialogs/contacts/models/contact-data-type';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { Attribute } from 'src/app/models/attribute';
import { Email } from 'src/app/models/email';
import { PropertyType } from 'src/app/models/enums/property-type';
import { IEmailTemplate } from 'src/app/models/interfaces/email-template.interface';
import { IEmail } from 'src/app/models/interfaces/email.interface';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { MemoryService } from 'src/app/services/memory.service';
import { environment } from 'src/environments/environment';
import { FileUploaderCustom } from '../../../controls/common/file-uploader-custom';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { AvmFileSizePipePipe } from '../../../controls/pipes/avm-file-size-pipe.pipe';
import { EmailsTemplatesFormContext } from '../emails-tempaltes-form-context';
import { TemplateContextMenuItem } from './Interfaces/template-context-menu-item';
import { TemplateContextMenuItemEvent } from './Interfaces/template-context-menu-item-event';
import { TemplateContextMenuComponent } from './template-context-menu/template-context-menu.component';

export enum AddressType {
    to,
    cc,
    cci,
}

@Component({
    selector: 'app-email-template-form',
    templateUrl: './email-template-form.component.html',
    styleUrls: ['./email-template-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatIconModule,
        MatTooltipModule,
        FileUploadModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatSelectModule,
        MatOptionModule,
        MatChipsModule,
        AngularEditorModule,
        TemplateContextMenuComponent,
        NgStyle,
        MatButtonModule,
        NgClass,
        AvmFileSizePipePipe,
        RichToolTipDirective,
    ],
})
export class EmailTemplateFormComponent extends ManagementFormComponent<IEmailTemplate> implements OnInit, OnChanges {
    /**
     * Composant du menu contextuel
     */
    @ViewChild(TemplateContextMenuComponent)
    contextMenu: TemplateContextMenuComponent;

    /**
     * URL pour le webService
     */
    get baseUrl() {
        if (environment.production) {
            return '';
        } else {
            return environment.serverUrl;
        }
    }

    /**
     * Liste des icônes utilisés dans le template
     */
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public AddressType = AddressType;

    /**
     * Modèle de l'email.
     */
    public email: IEmail = new Email();

    /**
     * Valeur indiquant si l'adresse To est invalide.
     */
    public toAddressIsInvalid: boolean = false;

    /**
     * Valeur indiquant si l'adresse CC est invalide.
     */
    public ccAddressIsInvalid: boolean = false;

    /**
     * Valeur indiquant si l'adresse CCi est invalide.
     */
    public cciAddressIsInvalid: boolean = false;

    /**
     * Formulaire du sujet du mail.
     */
    public subjectFormControl = new UntypedFormControl(null);

    /**
     * Formulaire du body du mail.
     */
    public bodyFormControl = new UntypedFormControl(null);
    public addressToFormControl = new UntypedFormControl(null);
    public addressCcFormControl = new UntypedFormControl(null);
    public addressCciFormControl = new UntypedFormControl(null);

    /**
     * Touches utilisées pour valider la saisie d'une adresse.
     */
    readonly separatorKeysCodes: number[] = [ENTER, 190, COMMA];

    /**
     * Valeur indiquant si le menu contextuel est visible.
     */
    isDisplayContextMenu: boolean;

    /**
     * Liste des éléments du menu contextuel.
     */
    contextMenuItems: Array<TemplateContextMenuItem> = [];

    /**
     * Position sur l'axe X de la sourris au moement d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionX: number;

    /**
     * Position sur l'axe Y de la sourris au moement d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionY: number;

    /**
     * Index du début du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionStart: number;

    /**
     * Index de fin du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionEnd: number;

    /**
     * Texte sélectionné dans le champ body (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentRange: Range;

    /**
     * Liste des attributs personnalisés de l'organisation (utilisé par le menu contextuel d'ajout de variables).
     */
    attributes: Attribute[] = [];

    /**
     * Configuration de l'editeur HTML du body en mode édition.
     */
    edit_config: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        sanitize: false,
        placeholder: 'Ecrire votre message ici ...',
        translate: 'no',
        defaultParagraphSeparator: 'DIV',
        defaultFontName: 'Arial',
        toolbarHiddenButtons: [['insertVideo']],
        customClasses: [
            {
                name: 'quote',
                class: 'quote',
            },
            {
                name: 'redText',
                class: 'redText',
            },
            {
                name: 'titleText',
                class: 'titleText',
                tag: 'h1',
            },
        ],
    };

    /**
     * Configuration de l'editeur HTML du body en mode lecture.
     */
    read_config: AngularEditorConfig = {
        editable: false,
        showToolbar: false,
        spellcheck: true,
        height: '15rem',
        placeholder: 'Ecrire votre message ici ...',
        translate: 'no',
        defaultParagraphSeparator: 'DIV',
        defaultFontName: 'Arial',
        toolbarHiddenButtons: [['insertVideo']],
        customClasses: [
            {
                name: 'quote',
                class: 'quote',
            },
            {
                name: 'redText',
                class: 'redText',
            },
            {
                name: 'titleText',
                class: 'titleText',
                tag: 'h1',
            },
        ],
    };

    /**
     * Infos du mail à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('emailTemplate')
    public dataInput: Required<IEmailTemplate> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: EmailsTemplatesFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<EmailsTemplatesFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: EmailsTemplatesFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('emailTemplateUpdated')
    public dataOutput: EventEmitter<IEmailTemplate> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Composant FileUploaderCustom
     */
    public uploader: FileUploaderCustom = null;

    /**
     * Constructeur
     */
    constructor(
        public dialog: MatDialog,
        private memoryService: MemoryService,
        private emailsTemplatesService: EmailsTemplatesService,
        private attributesService: AttributeDefinitionsService,
        private dialogService: DialogService
    ) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            if (!!x.item.template) {
                this.email = x.item.template;

                if (x.item.template.fromAddressId === null) {
                    x.item.template.fromAddressId = -1;
                }

                this.bodyFormControl.setValue(this.email.body);
                this.subjectFormControl.setValue(this.email.subject);
            } else {
                this.email = {
                    fromAddressId: -1,
                    to: [],
                    cc: [],
                    cci: [],
                    subject: null,
                    body: null,
                    attachments: new Map<string, File>(),
                    predefinedAttachments: [],
                };
                this.bodyFormControl.setValue(null);
                this.subjectFormControl.setValue(null);
            }

            if (this.uploader) {
                this.uploader.clear();
            }

            if (this._context.mode == FormMode.Reading) {
                this.subjectFormControl.disable();
                this.addressToFormControl.disable();
                this.addressCcFormControl.disable();
                this.addressCciFormControl.disable();
            }
        });

        let url: string;

        if (this._context.global === true) {
            url = this.baseUrl + `/api/organizations/${this.memoryService.getSelectedOrganization().id}}/emailtemplates`;
        } else {
            url = this.baseUrl + `/api/organizations/${this.memoryService.getSelectedOrganization().id}}/views/${this.memoryService.getSelectedView().id}/emailtemplates`;
        }

        this.uploader = new FileUploaderCustom({
            url: url,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${!this.memoryService.getConnectedUser().token.accessToken}`,
        });

        if (this._context.global !== true) {
            this.attributesService.List(null, null, null, null).subscribe((x) => {
                this.attributes = x.value;
            });
        } else {
            this.attributesService.SystemList(null, null, null, null).subscribe((x) => {
                this.attributes = x.value;
            });
        }

        this.addressToFormControl.valueChanges.subscribe((v) => {
            if (this.toAddressIsInvalid === true) {
                this.addressToFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCcFormControl.valueChanges.subscribe((v) => {
            if (this.ccAddressIsInvalid === true) {
                this.addressCcFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCciFormControl.valueChanges.subscribe((v) => {
            if (this.cciAddressIsInvalid === true) {
                this.addressCciFormControl.setErrors({ invalidFormat: true });
            }
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();

        if (this._context?.mode == FormMode.Reading) {
            this.subjectFormControl.disable();
            this.addressToFormControl.disable();
            this.addressCcFormControl.disable();
            this.addressCciFormControl.disable();
        } else {
            this.subjectFormControl.enable();
            this.addressToFormControl.enable();
            this.addressCcFormControl.enable();
            this.addressCciFormControl.enable();
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IEmailTemplate> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            name: new UntypedFormControl(null, Validators.required),
            template: new UntypedFormControl(this.email),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        this.email.body = this.bodyFormControl.value;

        if (this.email.fromAddressId === -1) {
            this.email.fromAddressId = null;
        }

        this.formGroup.get('template').setValue(this.email);
        super.manualSave();
    }

    /**
     * Ajoute une adresse
     * @param event : Adresse à ajouter.
     * @param AddressType : Type d'adresse.
     */
    public addAddress(event: MatChipInputEvent, addressType: AddressType): void {
        const input = event.chipInput.inputElement;
        const value = event.value.trim();

        if (value !== '') {
            if (!!value && new RegExp(this.emailPattern).test(value)) {
                input.value = '';
                switch (addressType) {
                    case AddressType.to:
                        this.email.to.push(value);
                        this.toAddressIsInvalid = false;
                        this.addressToFormControl.markAsPristine();
                        this.addressToFormControl.markAsUntouched();
                        break;
                    case AddressType.cc:
                        this.email.cc.push(value);
                        this.ccAddressIsInvalid = false;
                        this.addressCcFormControl.markAsPristine();
                        this.addressCcFormControl.markAsUntouched();
                        break;
                    case AddressType.cci:
                        this.email.cci.push(value);
                        this.cciAddressIsInvalid = false;
                        this.addressCciFormControl.markAsPristine();
                        this.addressCciFormControl.markAsUntouched();
                        break;
                }
            } else {
                switch (addressType) {
                    case AddressType.to:
                        this.toAddressIsInvalid = true;
                        break;
                    case AddressType.cc:
                        this.ccAddressIsInvalid = true;
                        break;
                    case AddressType.cci:
                        this.cciAddressIsInvalid = true;
                        break;
                }
            }
        } else {
            switch (addressType) {
                case AddressType.to:
                    this.toAddressIsInvalid = false;
                    break;
                case AddressType.cc:
                    this.ccAddressIsInvalid = false;
                    break;
                case AddressType.cci:
                    this.cciAddressIsInvalid = false;
                    break;
            }
        }

        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Supprime une adresse.
     * @param address : Adresse à supprimer
     * @param addressType : Type d'adresse
     */
    public removeAddress(address: string, addressType: AddressType): void {
        switch (addressType) {
            case AddressType.to:
                this.email.to.splice(this.email.to.indexOf(address), 1);
                break;
            case AddressType.cc:
                this.email.cc.splice(this.email.cc.indexOf(address), 1);
                break;
            case AddressType.cci:
                this.email.cci.splice(this.email.cci.indexOf(address), 1);
                break;
        }

        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Levé lorsque le sujet du mail change.
     * @event : événement clavier.
     */
    public onChangeSubject(event): void {
        console.log(event.target.value);
        this.email.subject = event.target.value;
        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Levé lorsque le corps du mail change.
     */
    public onChangeBody(): void {
        this.email.body = this.bodyFormControl.value;
        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Levé lorsque l'émetteur du mail change.
     */
    public onSenderChange(event): void {
        this.email.fromAddressId = event.value;
        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Levé lorsque l'utilisateur ajoute des pièce-jointes.
     * @param fileItems : Liste des pièces-jointes
     */
    public onFileSelected(fileItems: FileList) {
        console.log('onAfterAddingFile : ' + fileItems);

        if (!this.email.attachments) {
            this.email.attachments = new Map<string, File>();
        }

        for (let index: number = 0; index < fileItems.length; index++) {
            this.email.attachments.set(fileItems[index].name, fileItems[index]);
        }

        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Affiche la boite de dialogue des contacts
     */
    public contacts(): void {
        const dialogRef = this.dialogService.openContacts(ContactDataType.Email).subscribe((x) => {
            if (!!x) {
                if (this.email.to.length == 0) {
                    this.toAddressIsInvalid = false;
                }

                x.toAddresses.forEach((to) => {
                    this.email.to.push(to);
                });

                x.ccAddresses.forEach((cc) => {
                    this.email.cc.push(cc);
                });

                x.cciAddresses.forEach((cci) => {
                    this.email.cci.push(cci);
                });

                if (this.email.to.length == 0) {
                    this.toAddressIsInvalid = true;
                }
            }
        });
    }

    /**
     * Supprime une pièce-jointe
     * @param attachmentId : Id de la pièce-jointe.
     */
    public removePredefinedAttachment(attachmentId) {
        this.emailsTemplatesService.deleteAttachment(this._context.item.id, attachmentId, this._context.global).subscribe((x) => {
            let context = this._context;

            context.item.template.predefinedAttachments = x.template.predefinedAttachments;

            this._context$.next(context);
        });
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur le body.
     * @param event : Événement souris.
     */
    public onBodyRightClick(event: MouseEvent) {
        this.isDisplayContextMenu = true;

        this.contextMenuItems = this.getContextMenuItems('body');

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le body. Le paramètre remplacera cette selection.
        let selection = window.getSelection();

        if (selection.getRangeAt && selection.rangeCount) {
            this.currentRange = selection.getRangeAt(0).cloneRange();
        }

        this.contextMenu.openMenu(event);
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant listant des adresses.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onChipListRightClick(source: string, event: MouseEvent) {
        this.isDisplayContextMenu = true;

        this.contextMenuItems = this.getContextMenuItems(source);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        if (this.contextMenuItems.length > 0) {
            this.contextMenu.openMenu(event);
        }
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant Input.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.isDisplayContextMenu = true;

        this.contextMenuItems = this.getContextMenuItems(source);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le champ Input. Le paramètre remplacera cette selection.
        this.currentSelectionStart = event.target['selectionStart'];
        this.currentSelectionEnd = event.target['selectionEnd'];

        this.contextMenu.openMenu(event);
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    handleMenuItemClick(event: TemplateContextMenuItemEvent) {
        switch (event.source) {
            case 'body':
                this.currentRange.insertNode(document.createTextNode(event.variable));
                this.bodyFormControl.setValue(document.getElementsByClassName('angular-editor-textarea')[0].innerHTML);
                break;
            case 'subject':
                let subject = this.subjectFormControl.value === null ? '' : this.subjectFormControl.value;
                this.email.subject = subject.substring(0, this.currentSelectionStart) + event.variable + subject.substring(this.currentSelectionEnd);
                this.subjectFormControl.setValue(this.email.subject);
                break;
            case 'to':
                this.appendParameterToAddress(this.email.to, event.variable);
                break;
            case 'cc':
                this.appendParameterToAddress(this.email.cc, event.variable);
                break;
            case 'cci':
                this.appendParameterToAddress(this.email.cci, event.variable);
                break;
        }

        this.isDisplayContextMenu = false;
    }

    /**
     * Ajoute un paramètre à une liste d'adresses.
     * @param addresses : Liste d'adresses.
     * @param parameter : paramètre à ajouter à la liste d'adresses.
     */
    appendParameterToAddress(addresses: string[], parameter: string) {
        addresses.push(parameter);
        this.cciAddressIsInvalid = false;
        this.formGroup.get('template').setValue(this.email);
    }

    /**
     * Retourne le style du menu contextuel.
     * @returns : Le style du menu contextuel.
     */
    getContextMenuStyle() {
        // Positionne le menu contextuel au niveau du pointeur de la souris.
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`,
        };
    }

    /**
     * Retourne les éléments du menu contextuel affichant les paramètres.
     * @param source : source du menu contextuel. Il s'agit du composant pour lequel le menu contextuel doit être affiché.
     * @returns La liste des éléments du menu contextuel.
     */
    getContextMenuItems(source: string) {
        let menuItems = [];

        if (source === 'to' || source === 'cc' || source === 'cci') {
            this.attributes
                .filter((x) => x.type === PropertyType.Email)
                .sort((x, y) => x.header.localeCompare(y.header))
                .forEach((attribute) => {
                    if (!attribute.custom) {
                        menuItems.push(
                            new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.${attribute.name[0].toUpperCase() + attribute.name.substring(1)}}}`, source)
                        );
                    } else {
                        menuItems.push(new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.CustomAttributes["${attribute.name}"]}}`, source));
                    }
                });
        } else {
            // Elements du menu contextuel commun à toutes les organisations.
            menuItems.push(new TemplateContextMenuItem('Url de suivi du dossier', '{{data.TrackingUrl}}', source));

            // Elements du menu contextuel propre à l'organisation courante.
            this.attributes
                .sort((x, y) => x.header.localeCompare(y.header))
                .forEach((attribute) => {
                    if (!attribute.custom) {
                        menuItems.push(
                            new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.${attribute.name[0].toUpperCase() + attribute.name.substring(1)}}}`, source)
                        );
                    } else {
                        menuItems.push(new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.CustomAttributes["${attribute.name}"]}}`, source));
                    }
                });
        }

        return menuItems;
    }

    /**
     * Ferme le menu contextuel.
     */
    public CloseContextMenu() {
        this.isDisplayContextMenu = false;
    }

    /**
     * Met en forme un texte pour que sa première lettre soit en majuscule.
     * @param text : Texte à mettre en forme.
     * @returns Le texte mis en forme.
     */
    capitalize(text: string) {
        if (typeof text !== 'string') return '';
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
